import React from 'react';
import StickerPreview from './StickerPreview';

const ThermalLabelPreview = ({ 
  layout = 'classic', 
  data = {}, 
  showQR = true,
  qrPosition = 'top', // 'top', 'bottom', 'left', 'right'
  className = '',
  labelSize = '2x1', // Common thermal label sizes
  customWidth = null,
  customHeight = null,
  showRoll = true,
  nameFontSize = null,
  positionFontSize = null,
  orgFontSize = null,
  lineHeight = 1.2,
  logoSize = 40,
  showLogo = false,
  autoScaleFont = true,
  nameFontWeight = 'bold',
  positionFontWeight = 'semibold',
  orgFontWeight = 'semibold'
}) => {
  // Common thermal label sizes (in pixels at 203 DPI - typical for thermal printers)
  const thermalLabelSizes = {
    '1x1': { width: 203, height: 203, name: '1" × 1" Square' },
    '2x1': { width: 406, height: 203, name: '2" × 1" Rectangle' },
    '2.25x1.25': { width: 457, height: 254, name: '2.25" × 1.25" Name Tag' },
    '3x1': { width: 609, height: 203, name: '3" × 1" Banner' },
    '3x2': { width: 609, height: 406, name: '3" × 2" Large' },
    '4x2': { width: 812, height: 406, name: '4" × 2" Shipping' },
    '4x3': { width: 812, height: 609, name: '4" × 3" Large Square' },
    '4x6': { width: 812, height: 1218, name: '4" × 6" Postcard' }
  };

  // Get actual label dimensions
  const actualLabelSize = customWidth && customHeight 
    ? { width: customWidth, height: customHeight, name: 'Custom Size' }
    : thermalLabelSizes[labelSize];

  // Calculate scale for preview (thermal labels can be quite large)
  const previewScale = Math.min(250 / actualLabelSize.width, 150 / actualLabelSize.height, 0.6);
  const scaledLabel = {
    width: actualLabelSize.width * previewScale,
    height: actualLabelSize.height * previewScale
  };

  // Roll width for thermal printers (typically 4" max width)
  const rollWidth = 812; // 4 inches at 203 DPI
  const scaledRollWidth = rollWidth * previewScale;

  if (!showRoll) {
    // Just show the label at preview size
    return (
      <div className="flex flex-col items-center space-y-2">
        <div className="text-xs text-gray-500 font-medium">
          Thermal Label: {actualLabelSize.name}
        </div>
        <div className="text-xs text-gray-400">
          Actual: {(actualLabelSize.width / 203).toFixed(2)}" × {(actualLabelSize.height / 203).toFixed(2)}" 
          | Preview: {Math.round(scaledLabel.width)}×{Math.round(scaledLabel.height)}px
        </div>
        <StickerPreview
          layout={layout}
          data={data}
          showQR={showQR}
          qrPosition={qrPosition}
          className={className}
          size="normal"
          customWidth={scaledLabel.width}
          customHeight={scaledLabel.height}
          nameFontSize={nameFontSize}
          positionFontSize={positionFontSize}
          orgFontSize={orgFontSize}
          lineHeight={lineHeight}
          logoSize={logoSize}
          showLogo={showLogo}
          autoScaleFont={autoScaleFont}
          nameFontWeight={nameFontWeight}
          positionFontWeight={positionFontWeight}
          orgFontWeight={orgFontWeight}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-3 max-w-sm mx-auto">
      {/* Thermal Printer Info */}
      <div className="text-center">
        <div className="text-sm font-medium text-gray-700">
          Thermal Label Printer Preview
        </div>
        <div className="text-xs text-gray-500">
          Label: {actualLabelSize.name} | Roll Width: 4" max | Resolution: 203 DPI
        </div>
        <div className="text-xs text-gray-400 mt-1">
          Preview scaled to {Math.round(previewScale * 100)}% for display
        </div>
      </div>

      {/* Thermal Roll with Label */}
      <div className="relative w-full flex justify-center">
        {/* Roll Background */}
        <div 
          className="relative bg-white border-2 border-gray-300 shadow-lg rounded-lg overflow-hidden"
          style={{
            width: `${Math.min(scaledRollWidth, 300)}px`,
            height: `${Math.max(scaledLabel.height + 30, 100)}px`,
          }}
        >
          {/* Roll texture/perforations */}
          <div className="absolute inset-0 opacity-10">
            <div className="w-full h-full" 
                 style={{
                   backgroundImage: `
                     repeating-linear-gradient(
                       0deg,
                       transparent,
                       transparent 2px,
                       rgba(0,0,0,0.1) 2px,
                       rgba(0,0,0,0.1) 3px
                     )
                   `,
                   backgroundSize: '100% 20px'
                 }}>
            </div>
          </div>

          {/* Perforation line at top */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 border-b border-dashed border-gray-400"></div>

          {/* Label positioned on roll */}
          <div 
            className="absolute flex items-center justify-center"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: `${scaledLabel.width}px`,
              height: `${scaledLabel.height}px`,
            }}
          >
            <StickerPreview
              layout={layout}
              data={data}
              showQR={showQR}
              qrPosition={qrPosition}
              className="shadow-sm border border-gray-200"
              size="normal"
              customWidth={scaledLabel.width}
              customHeight={scaledLabel.height}
              nameFontSize={nameFontSize}
              positionFontSize={positionFontSize}
              orgFontSize={orgFontSize}
              lineHeight={lineHeight}
              logoSize={logoSize}
              showLogo={showLogo}
              autoScaleFont={autoScaleFont}
              nameFontWeight={nameFontWeight}
              positionFontWeight={positionFontWeight}
              orgFontWeight={orgFontWeight}
            />
          </div>

          {/* Roll edges */}
          <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-r from-gray-300 to-transparent"></div>
          <div className="absolute top-0 right-0 w-2 h-full bg-gradient-to-l from-gray-300 to-transparent"></div>
        </div>
      </div>

      {/* Thermal Printer Instructions */}
      <div className="text-center w-full">
        <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded-lg">
          <div className="font-medium text-blue-800 mb-1">Thermal Printer Setup</div>
          <div className="text-blue-700 text-left space-y-0.5">
            <div>• <strong>Label Size:</strong> {actualLabelSize.name}</div>
            <div>• <strong>Print Quality:</strong> 203 DPI (standard thermal)</div>
            <div>• <strong>Connection:</strong> USB or Wireless</div>
            <div>• <strong>Settings:</strong> Direct thermal, no ribbon needed</div>
          </div>
        </div>
      </div>

      {/* Compatible Label Sizes */}
      <div className="text-center w-full">
        <div className="text-xs text-gray-500 mb-1">Popular Thermal Label Sizes:</div>
        <div className="flex flex-wrap justify-center gap-1">
          {Object.entries(thermalLabelSizes).slice(0, 4).map(([key, size]) => (
            <span 
              key={key}
              className={`text-xs px-1.5 py-0.5 rounded ${
                labelSize === key 
                  ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {size.name.replace(' × ', '×')}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThermalLabelPreview;
